import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import Header from "./Header";
import Footer from "./Footer";
import Sidebar from "./Sidebar";
import { Outlet, Link, useLocation, matchPath, useNavigate } from "react-router-dom";
import routeMeta from "../../routes/Routes";
import routeConfig from "../../routes/Config";
import HeaderActions from "./HeaderActions";
import { apiClient } from "../api/ApiClient";

function Breadcrumbs({ breadcrumb }) {
    if (!breadcrumb || !breadcrumb.length) {
        return (
            <ul className="breadcrumb">
                <li className="breadcrumb-item">
                    <Link to="/">داشبورد</Link>
                </li>
            </ul>
        );
    }

    return (
        <ul className="breadcrumb">
            {breadcrumb.map((item, index) => {
                const isLast = index === breadcrumb.length - 1;

                if (isLast) {
                    return (
                        <li key={index} className="breadcrumb-item">
                            {item}
                        </li>
                    );
                }

                if (item === "داشبورد") {
                    return (
                        <li key={index} className="breadcrumb-item">
                            <Link to="/">{item}</Link>
                        </li>
                    );
                }

                return (
                    <li key={index} className="breadcrumb-item">
                        {item}
                    </li>
                );
            })}
        </ul>
    );
}

function normalizePath(pathname) {
    if (pathname === "/") return "/";
    return pathname.endsWith("/") ? pathname : pathname + "/";
}

function Layout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [subscriptionMessage, setSubscriptionMessage] = useState("");

    const allowedPaths = ['/dashboard/', '/subscription/general/'];

    useEffect(() => {
        const isAllowed = allowedPaths.some(p => location.pathname.startsWith(p));
        if (isAllowed) {
            setShowSubscriptionModal(false);
            return;
        }

        const token = localStorage.getItem('access_token');
        if (!token) return;

        const checkSubscription = async () => {
            try {
                const data = await apiClient('/subscribe/api/active/?module=moadian');
                if (data.active === false) {
                    setSubscriptionMessage("اشتراک شما فعال نیست یا تمام شده است. لطفاً تمدید کنید.");
                    setShowSubscriptionModal(true);
                    navigate('/subscription/general/');
                } else {
                    setShowSubscriptionModal(false);
                }
            } catch (err) {
                console.error("Error checking subscription:", err);
                setShowSubscriptionModal(false);
            }
        };

        checkSubscription();
    }, [location.pathname]);

    useEffect(() => {
        const handleSubscriptionInactive = (event) => {
            setSubscriptionMessage(event.detail || "اشتراک شما فعال نیست یا تمام شده است. لطفاً تمدید کنید.");
            setShowSubscriptionModal(true);
            navigate('/subscription/general/');
        };

        window.addEventListener('subscription_inactive', handleSubscriptionInactive);

        return () => {
            window.removeEventListener('subscription_inactive', handleSubscriptionInactive);
        };
    }, []);

    const activeRoute = routeConfig.find(route => matchPath({ path: route.path, end: false }, location.pathname));
    const meta = activeRoute ? activeRoute.meta : (routeMeta["/"] || {});
    const pageTitle = meta?.title;
    const breadcrumb = meta?.breadcrumb;

    useEffect(() => {
        // Suppress specific Bootstrap tooltip error that happens on race conditions during navigation
        const handleGlobalError = (event) => {
            if (event?.error?.message?.includes("reading 'template'") ||
                (event?.message && event.message.includes("reading 'template'"))) {
                event.preventDefault();
                console.warn("Ignored known Bootstrap tooltip race condition error.");
            }
        };

        window.addEventListener('error', handleGlobalError);
        window.addEventListener('unhandledrejection', handleGlobalError);

        if (!window.jQuery) return;
        const $ = window.jQuery;

        const initMenuButtons = () => {
            if (localStorage.getItem("nexel-classic-dashboard-menu-mini-theme") === "menu-mini-theme") {
                $("html").addClass("minimenu");
                $("#menu-mini-button").hide();
                $("#menu-expend-button").show();
            } else {
                $("html").removeClass("minimenu");
                $("#menu-expend-button").hide();
                $("#menu-mini-button").show();
            }

            $("#menu-mini-button").off("click").on("click", function () {
                $("#menu-mini-button").hide();
                $("#menu-expend-button").show();
                $("html").addClass("minimenu");
                localStorage.setItem("nexel-classic-dashboard-menu-mini-theme", "menu-mini-theme");
            });

            $("#menu-expend-button").off("click").on("click", function () {
                $("#menu-expend-button").hide();
                $("#menu-mini-button").show();
                $("html").removeClass("minimenu");
                localStorage.setItem("nexel-classic-dashboard-menu-mini-theme", "menu-expend-theme");
            });
        };

        const initMobileCollapse = () => {
            $("#mobile-collapse").off("click").on("click", function (e) {
                e.preventDefault();
                $("html").toggleClass("nxl-mobile-menu-open");
                $(this).find(".hamburger").toggleClass("is-active");
            });
        };

        const initMegaMenu = () => {
            $("#nxl-lavel-mega-menu-open").off("click").on("click", function (e) { e.preventDefault(); $("html").addClass("nxl-lavel-mega-menu-open"); });
            $("#nxl-lavel-mega-menu-hide").off("click").on("click", function (e) { e.preventDefault(); $("html").removeClass("nxl-lavel-mega-menu-open"); });
        };

        const initSidebarMenu = () => {
            $(".nxl-hasmenu > .nxl-link").off("click").on("click", function (e) {
                e.preventDefault();
                const $parent = $(this).parent();
                const $submenu = $parent.find(".nxl-submenu").first();
                if ($submenu.length) {
                    $parent.siblings(".nxl-hasmenu").find(".nxl-submenu").slideUp(300);
                    $parent.siblings(".nxl-hasmenu").removeClass("open");
                    $parent.toggleClass("open");
                    $submenu.slideToggle(300);
                }
            });
        };

        const initTooltips = () => {
            try {
                // Safely dispose old tooltips
                $('[data-bs-toggle="tooltip"]').each(function () {
                    try { $(this).tooltip('dispose'); } catch (e) { }
                });

                $('.sidebar-tooltip').tooltip({
                    placement: 'left',
                    trigger: 'hover',
                    container: 'body',
                    boundary: 'viewport',
                    offset: [5, 25],
                    html: true,
                    fallbackPlacements: ['top', 'bottom']
                });

                $('[data-bs-toggle="tooltip"]:not(.sidebar-tooltip)').tooltip({
                    trigger: 'hover',
                    container: 'body'
                });
            } catch (err) {
                console.warn('Error initializing tooltips:', err);
            }
        };

        initMenuButtons();
        initMobileCollapse();
        initMegaMenu();
        initSidebarMenu();

        const initDarkMode = () => {
            const savedSkin = localStorage.getItem("app-skin-dark");
            if (savedSkin === "app-skin-dark") {
                $("html").addClass("app-skin-dark");
                $(".dark-button").hide();
                $(".light-button").show();
            } else {
                $("html").removeClass("app-skin-dark");
                $(".dark-button").show();
                $(".light-button").hide();
            }

            $(".dark-button").off("click").on("click", function (e) {
                e.preventDefault();
                $(".dark-button").hide();
                $(".light-button").show();
                $("html").addClass("app-skin-dark");
                localStorage.setItem("app-skin-dark", "app-skin-dark");
            });

            $(".light-button").off("click").on("click", function (e) {
                e.preventDefault();
                $(".light-button").hide();
                $(".dark-button").show();
                $("html").removeClass("app-skin-dark");
                localStorage.setItem("app-skin-dark", "app-skin-light");
            });
        };

        initDarkMode();

        const t1 = setTimeout(initTooltips, 400);
        const t2 = setTimeout(initTooltips, 1000);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            try {
                // Defensive cleanup
                $('[data-bs-toggle="tooltip"]').each(function () {
                    try { window.jQuery(this).tooltip('dispose'); } catch (e) { }
                });
                $('.tooltip').remove();
            } catch (err) {
                // ignore
            }
        };
    }, [location.pathname]);

    // Global error suppression for the known Bootstrap tooltip race condition
    useEffect(() => {
        const handleGlobalError = (event) => {
            if (event?.error?.message?.includes("reading 'template'") ||
                (event?.message && event.message.includes("reading 'template'"))) {
                event.preventDefault();
                console.warn("Suppressing Bootstrap tooltip error.");
            }
        };
        window.addEventListener('error', handleGlobalError);
        return () => window.removeEventListener('error', handleGlobalError);
    }, []);

    return (
        <>
            <Header />
            <Sidebar />
            <main className="nxl-container">
                <div className="nxl-content">
                    {showSubscriptionModal && (
                        <div className="alert alert-danger mx-3 mt-3 text-center fw-bold fs-5">
                            <i className="feather-alert-triangle me-2"></i>
                            {subscriptionMessage}
                        </div>
                    )}
                    <div style={showSubscriptionModal ? { pointerEvents: 'none', opacity: 0.6, userSelect: 'none' } : {}}>
                        <div className="page-header">
                            <div className="page-header-right d-flex align-items-center">
                                <div className="page-header-title">
                                    <h5 className="m-b-10">{pageTitle}</h5>
                                </div>
                                <Breadcrumbs breadcrumb={breadcrumb} />
                            </div>
                            <div className="page-header-right ms-auto">
                                <div className="page-header-right-items">
                                    <HeaderActions />
                                </div>
                            </div>
                        </div>

                        <div className="main-content">
                            <Outlet />
                        </div>
                    </div>
                    <Footer />
                </div>
            </main>
        </>
    );
}

export default Layout;